import BossModel from "@/models/BossModel";
import Group from "@/models/Proposal/Group/Group";
import ProposalSurcharge from "@/models/Proposal/ProposalSurcharge/ProposalSurcharge";
import Placeholder from "@/models/Proposal/Placeholder/Placeholder";
import Cancellation from "@/models/Proposal/Cancellation/Cancellation";
import Country from "@/models/Country/Country";
import Dossier from "@/models/Dossier/Dossier";
import File from "@/models/File/File";
import constants from "@/constants";
import PaymentTerm from "@/models/Proposal/PaymentTerm/PaymentTerm";
import ProposalNonClosingDay from "@/models/Proposal/ProposalNonClosingDay/ProposalNonClosingDay";
import User from "@/models/User/User";
import moment from "moment";
import DebitInvoice from "@/models/Proposal/Invoice/DebitInvoice";
import CreditInvoice from "@/models/Proposal/Invoice/CreditInvoice";
import ProposalDownload from "@/models/Proposal/ProposalDownload/ProposalDownload";
import Favorite from "@/models/Proposal/Favorite/Favorite";
import uniqBy from "lodash/uniqBy";
import orderBy from "lodash/orderBy";
import map from "lodash/map";
import OccurredEvent from "@/models/Proposal/OccurredEvent/OccurredEvent";
import Presentation from "@/models/Proposal/Presentation/Presentation";
import Product from "@/models/Proposal/Product/Product";
import DossierTicket from "@/models/Dossier/Ticket/Ticket";
import Ticket from "@/models/Proposal/Ticket/Ticket";
import Upsell from "@/models/Proposal/Upsell/Upsell";
import PnrCode from "@/models/Dossier/Ticket/PnrCode/PnrCode.js";
import AreAllProposalProductsCheckAcceptedQuery from "@/queries/Proposal/AreAllProposalProductsCheckAcceptedQuery.js";
import AreAllProposalTicketsCheckAcceptedQuery from "@/queries/Proposal/AreAllProposalTicketsCheckAcceptedQuery.js";

export default class Proposal extends BossModel {
    /**
     *
     * @type {string}
     */
    static apiUrl = "proposal";

    /**
     *
     * @type {string}
     */
    static entity = "proposals";

    static fields() {
        return {
            id: this.number(null).nullable(),
            public_id: this.attr(null).nullable(),
            dossier_id: this.number(null).nullable(),
            created_at: this.attr(null),
            start_date: this.attr(null),
            end_date: this.attr(null),
            updated_at: this.attr(null),
            deleted_at: this.attr(null),
            approved_by: this.attr(null),
            is_from_digi: this.boolean(false),
            invoice_remarks: this.attr(null),
            invoice_title: this.attr(null),
            ticket_file_id: this.number(null).nullable(),
            is_ticket_file_active: this.boolean(true),
            voucher_file_id: this.number(null).nullable(),
            is_voucher_file_active: this.boolean(true),
            vouchers_generated: this.boolean(null).nullable(),
            is_published: this.boolean(false),
            is_marge_valid: this.boolean(false),
            last_offer_send: this.attr(null),
            customer_accepted_at: this.attr(null),
            offer_customer_declined_at: this.attr(null),
            offer_decline_reason: this.attr(null),
            employee_accepted_at: this.attr(null),
            booking_accepted_at: this.attr(null),
            manual_marge_cid: this.attr(null),
            parent_id: this.number(null).nullable(),
            file_id: this.number(null).nullable(),
            price_change: this.attr(null),
            is_file_manually_uploaded: this.boolean(false),
            is_visa_required: this.boolean(false),
            trip_country_id: this.number(null).nullable(),
            ref_number: this.attr(null),
            is_archived: this.boolean(false),
            is_agent_check_finished: this.boolean(false),
            sale_price_modification: this.attr(null),
            sale_price: this.number(null).nullable(),
            purchase_price: this.number(null).nullable(),
            agent_request_send_days_ago: this.number(null).nullable(),
            is_cfs_check_finished: this.boolean(false),
            price_guarantee_deadline_at: this.attr(null).nullable(),
            offer_send_scheduled_at: this.attr(null).nullable(),
            dossier: this.belongsTo(Dossier, "dossier_id"),
            ticketFile: this.belongsTo(File, "ticket_file_id"),
            voucherFile: this.belongsTo(File, "voucher_file_id"),
            cancellations: this.hasMany(Cancellation, "proposal_id"),
            creditInvoice: this.hasOne(CreditInvoice, "proposal_id"),
            debitInvoice: this.hasOne(DebitInvoice, "proposal_id"),
            proposalDownloads: this.hasMany(ProposalDownload, "proposal_id"),
            favorited: this.hasMany(Favorite, "proposal_id"),
            groups: this.hasMany(Group, "proposal_id"),
            nonClosingDays: this.hasMany(ProposalNonClosingDay, "proposal_id"),
            upsells: this.hasMany(Upsell, "proposal_id"),
            paymentTerms: this.hasMany(PaymentTerm, "proposal_id"),
            placeholders: this.hasMany(Placeholder, "proposal_id"),
            surcharges: this.hasMany(ProposalSurcharge, "proposal_id"),
            country: this.belongsTo(Country, "trip_country_id"),
            trip_country: this.belongsTo(Country, "trip_country_id"),
            parent: this.belongsTo(Proposal, "parent_id"),
            occurredEvents: this.hasMany(OccurredEvent, "proposal_id"),
            presentation: this.hasOne(Presentation, "proposal_id"),
            groupProducts: this.hasManyThrough(
                Product,
                Group,
                "proposal_id",
                "proposal_group_id",
            ),
            cancellationProducts: this.hasManyThrough(
                Product,
                Cancellation,
                "proposal_id",
                "proposal_cancellation_id",
            ),
            cancellationTickets: this.hasManyThrough(
                Ticket,
                Cancellation,
                "proposal_id",
                "proposal_cancellation_id",
            ),
            groupTickets: this.hasManyThrough(
                Ticket,
                Group,
                "proposal_id",
                "proposal_group_id",
            ),
        };
    }

    /**
     *
     * @param data
     * @returns {Promise<AxiosResponse<T>>}
     */
    static createByProposal(data) {
        return this.api().action("create-by-proposal").create(data);
    }

    /**
     *
     * @returns {*}
     */
    fetchPackageDayToDays() {
        return this.constructor
            .api()
            .with(["groups.package.package.packageDaytodays"])
            .find(this.id);
    }

    /**
     *
     * @returns {Promise<AxiosResponse<T>>}
     */
    Archive() {
        return this.constructor
            .api()
            .action(this.id + "/archive")
            .update(this);
    }

    /**
     *
     * @returns {Promise<AxiosResponse<T>>}
     */
    DeArchive() {
        return this.constructor
            .api()
            .action(this.id + "/de-archive")
            .update(this);
    }

    Duplicate() {
        return this.constructor
            .api()
            .action(this.id + "/duplicate")
            .create(this);
    }

    /**
     *
     * @returns {Promise<AxiosResponse<T>>}
     */
    ConfirmOffer() {
        return this.constructor
            .api()
            .action(this.id + "/confirm-offer")
            .update(this);
    }

    /**
     *
     * @param manualMargeCid
     * @returns {Promise<AxiosResponse<T>>}
     */
    UpdateManualMargeCheck(manualMargeCid = 1) {
        return this.constructor
            .api()
            .action(this.id + "/manual-marge-check/" + manualMargeCid)
            .update();
    }

    /**
     *
     * @returns {Promise<AxiosResponse<T>>}
     */
    AcceptOffer() {
        return this.constructor
            .api()
            .action(this.id + "/accept-offer")
            .update();
    }

    /**
     *
     * @returns {Promise<AxiosResponse<T>>}
     * @constructor
     */
    EmployeeAccept() {
        return this.constructor
            .api()
            .action(this.id + "/employee-accept")
            .update();
    }

    /**
     *
     * @returns {Promise<AxiosResponse<T>>}
     */
    SendOffer() {
        return this.constructor
            .api()
            .action(this.id + "/send-offer")
            .update();
    }

    /**
     *
     * @returns {Promise<AxiosResponse<T>>}
     */
    ApproveAllTicketCheck() {
        return this.constructor
            .api()
            .action(this.id + "/approve-ticket-check")
            .update();
    }

    /**
     *
     * @param reason
     * @returns {Promise<AxiosResponse<T>>}
     */
    DenyAllTicketsCheck(reason) {
        return this.constructor
            .api()
            .action(this.id + "/deny-ticket-check")
            .update({ reason: reason });
    }

    /**
     *
     * @returns {Promise<AxiosResponse<T>>}
     */
    ApproveAllProductCheck() {
        return this.constructor
            .api()
            .action(this.id + "/approve-product-check")
            .update();
    }

    /**
     *
     * @param reason
     * @returns {Promise<AxiosResponse<T>>}
     */
    DenyAllProductCheck(reason) {
        return this.constructor
            .api()
            .action(this.id + "/deny-product-check")
            .update({ reason: reason });
    }

    /**
     *
     * @returns {Promise<AxiosResponse<T>>}
     */
    AcceptBooking() {
        return this.constructor
            .api()
            .action(this.id + "/accept-booking")
            .update();
    }

    /**
     *
     * @returns {Promise<AxiosResponse<T>>}
     */
    AcceptCancellation() {
        return this.constructor
            .api()
            .action(this.id + "/accept-cancellation")
            .update();
    }

    /**
     *
     * @returns {Promise<AxiosResponse<T>>}
     */
    FinishAgentCheck() {
        return this.constructor
            .api()
            .action(this.id + "/finish-agent-check")
            .update();
    }

    /**
     *
     * @returns {Promise<AxiosResponse<T>>}
     */
    FinishCfsCheck() {
        return this.constructor
            .api()
            .action(this.id + "/finish-cfs-check")
            .update();
    }

    /**
     *
     * @returns {Promise<AxiosResponse<T>>}
     */
    Publish() {
        return this.constructor
            .api()
            .action(this.id + "/publish")
            .update();
    }

    async ValidatePnrTravellers() {
        let travellers;
        await PnrCode.api()
            .action("validate-travellers-by-proposal/" + this.id)
            .getValue()
            .then((data) => {
                travellers = data;
            });

        return travellers;
    }

    /**
     *
     * @returns {null|number}
     */
    GetDurationInDays() {
        if (this.start_date && this.end_date) {
            let diffInDays = moment(this.end_date).diff(
                moment(this.start_date),
                "days",
            );
            if (diffInDays > 1) {
                return diffInDays + 1;
            }

            return diffInDays;
        }

        return null;
    }

    /**
     *
     * @returns {*}
     */
    GetGroupPackages() {
        let packages = [];
        this.$query()
            .with("groups.groupPackage.*")
            .find(this.id)
            .groups.forEach((group) => {
                if (group.groupPackage) {
                    packages.push(group.groupPackage);
                }
            });

        return uniqBy(packages, "package_id");
    }

    /**
     *
     * @returns {boolean}
     */
    IsBookingAccepted() {
        return this.booking_accepted_at !== null;
    }

    get locked() {
        return (
            this.IsBookingAccepted() ||
            this.IsArchived() ||
            this.IsSendToCustomer() ||
            this.Dossier().IsCancelled() ||
            this.IsCustomerAccepted()
        );
    }

    IsMargeValid() {
        return this.is_marge_valid == true;
    }

    IsSendToCustomer() {
        return this.last_offer_send !== null;
    }

    IsCustomerAccepted() {
        return this.customer_accepted_at !== null;
    }

    IsCustomerDeclined() {
        return this.offer_customer_declined_at !== null;
    }

    IsEmployeeAccepted() {
        return this.employee_accepted_at !== null;
    }

    IsConditionsAccepted() {
        return (
            this.Dossier().company_conditions_accepted_at !== null &&
            this.Dossier().anvr_conditions_accepted_at
        );
    }

    /**
     *
     * @returns {*}
     */
    IsCfsCheckFinished() {
        return this.is_cfs_check_finished;
    }

    /**
     *
     * @returns {*}
     */
    IsAgentCheckFinished() {
        return this.is_agent_check_finished;
    }

    /**
     *
     * @returns {boolean|*}
     */
    IsCfsAccepted() {
        if (this.GroupProducts().length + this.GroupTickets().length === 0) {
            return true;
        }

        return (
            AreAllProposalProductsCheckAcceptedQuery(this.id) &&
            AreAllProposalTicketsCheckAcceptedQuery(this.id)
        );
    }

    /**
     *
     * @returns {boolean}
     */
    IsProductCheckOpen() {
        return (
            this.GroupProducts().filter((product) =>
                product.getRelated("checkState").IsOpen(),
            ).length > 0
        );
    }

    /**
     *
     * @returns {*}
     */
    IsProductCheckDenied() {
        let hasNoOpenLines =
            this.GroupProducts().filter((product) =>
                product.getRelated("checkState").IsOpen(),
            ).length === 0;
        let hasADeniedLine =
            this.GroupProducts().filter((product) =>
                product.getRelated("checkState").IsDenied(),
            ).length > 0;

        return hasNoOpenLines && hasADeniedLine;
    }

    /**
     *
     * @returns {boolean}
     */
    IsTicketCheckOpen() {
        return (
            this.GroupTickets().filter((ticket) =>
                ticket.getRelated("checkState").IsOpen(),
            ).length > 0
        );
    }

    /**
     *
     * @returns {*}
     */
    IsTicketCheckDenied() {
        let hasNoOpenLines =
            this.GroupTickets().filter((ticket) =>
                ticket.getRelated("checkState").IsOpen(),
            ).length === 0;
        let hasADeniedLine =
            this.GroupTickets().filter((ticket) =>
                ticket.getRelated("checkState").IsDenied(),
            ).length > 0;

        return hasNoOpenLines && hasADeniedLine;
    }

    /**
     *
     * @returns {boolean}
     */
    IsManualMargeCheckAccepted() {
        return (
            constants.actionStatuses.find(
                (stat) => stat.id === this.manual_marge_cid,
            ).key === "accepted"
        );
    }

    /**
     *
     * @returns {boolean}
     */
    IsManualMargeCheckInProgress() {
        return (
            constants.actionStatuses.find(
                (stat) => stat.id === this.manual_marge_cid,
            ).key === "actionRequired"
        );
    }

    /**
     *
     * @returns {boolean}
     */
    IsManualMargeCheckDenied() {
        return (
            constants.actionStatuses.find(
                (stat) => stat.id === this.manual_marge_cid,
            ).key === "denied"
        );
    }

    /**
     *
     * @returns {boolean}
     */
    IsManualMargeNoActionRequired() {
        return (
            constants.actionStatuses.find(
                (stat) => stat.id === this.manual_marge_cid,
            ).key === "noActionRequired"
        );
    }

    /**
     *
     * @returns {boolean}
     */
    IsPublished() {
        return this.is_published;
    }

    /**
     *
     * @returns {boolean}
     */
    IsArchived() {
        return this.is_archived;
    }

    /**
     *
     * @returns {boolean}
     */
    AreTicketsPrinted() {
        return this.GroupTickets().length === this.PrintedTickets().length;
    }

    /**
     *
     * @returns {boolean}
     */
    IsTicketGenerated() {
        return this.ticket_file_id !== null;
    }

    /**
     *
     * @returns {*[]}
     */
    AgentNotRequestedLines() {
        return this.GroupProducts().filter(
            (product) =>
                !product.getRelated("agentPreset.agentProduct.status") ||
                product
                    .getRelated("agentPreset.agentProduct.status")
                    .IsNotRequested(),
        );
    }

    /**
     *
     * @returns {*[]}
     */
    AgentRequestedLines() {
        return this.GroupProducts().filter(
            (product) =>
                product.getRelated("agentPreset.agentProduct.status") &&
                product
                    .getRelated("agentPreset.agentProduct.status")
                    .IsRequested(),
        );
    }

    /**
     *
     * @returns {*[]}
     */
    AgentConfirmedLines() {
        return this.GroupProducts().filter(
            (product) =>
                product.getRelated("agentPreset.agentProduct.status") &&
                product
                    .getRelated("agentPreset.agentProduct.status")
                    .IsConfirmed(),
        );
    }

    AgentDeniedLines() {
        return this.GroupProducts().filter(
            (product) =>
                product.getRelated("agentPreset.agentProduct.status") &&
                product
                    .getRelated("agentPreset.agentProduct.status")
                    .IsDenied(),
        );
    }

    /**
     *
     * @returns {*[]}
     */
    PrintedTickets() {
        return Ticket.query()
            .whereHas("group", (query) => {
                query.where("proposal_id", this.id);
            })
            .whereHas("dossierTicket", (query) => {
                query.where((ticket) => {
                    return ticket.printed_at !== null;
                });
            })
            .get();
    }

    /**
     *
     * @returns {boolean}
     */
    HasTicketsWithoutPrintDate() {
        return Ticket.query()
            .whereHas("group", (query) => {
                query.where("proposal_id", this.id);
            })
            .whereHas("dossierTicket", (query) => {
                query.where((ticket) => {
                    return ticket.print_at === null;
                });
            })
            .exists();
    }

    CancellationProducts() {
        return orderBy(
            this.getRelated("cancellationProducts"),
            "start_date",
            "asc",
        );
    }

    GroupProducts() {
        return orderBy(this.getRelated("groupProducts"), "start_date", "asc");
    }

    GroupTickets() {
        return this.getRelated("groupTickets");
    }

    OccurredEvents() {
        return orderBy(this.getRelated("occurredEvents"), "created_at", "desc");
    }

    /**
     *
     * @returns {boolean|*}
     */
    Country() {
        return this.getRelated("country");
    }

    /**
     *
     * @returns {boolean|*}
     */
    Dossier() {
        return this.getRelated("dossier");
    }

    /**
     *
     * @returns {*}
     */
    Favorited() {
        return this.getRelated("favorited");
    }

    /**
     *
     * @returns {*}
     */
    MyFavorited() {
        return this.Favorited().filter(
            (favorite) => favorite.user_id === User.loggedInUser().id,
        );
    }

    /**
     *
     * @returns {*}
     */
    DebitInvoice() {
        return this.getRelated("debitInvoice");
    }

    /**
     *
     * @returns {*}
     */
    CreditInvoice() {
        return this.getRelated("creditInvoice");
    }

    /**
     *
     * @returns {boolean|*}
     */
    TicketFile() {
        return this.getRelated("ticketFile");
    }

    /**
     *
     * @returns {boolean|*}
     */
    VoucherFile() {
        return this.getRelated("voucherFile");
    }

    /**
     *
     * @returns {*}
     */
    Upsell() {
        return this.getRelated("upsell");
    }

    /**
     *
     * @returns {*}
     */
    Groups() {
        return this.getRelated("groups");
    }

    /**
     *
     * @returns {*}
     */
    ProposalSurcharges() {
        return orderBy(this.getRelated("surcharges"), "id");
    }

    /**
     *
     * @returns {*}
     */
    ProposalNonClosingDays() {
        return this.getRelated("nonClosingDays");
    }

    /**
     *
     * @returns {*}
     */
    Placeholders() {
        return this.getRelated("placeholders");
    }

    /**
     *
     * @returns {boolean|*}
     */
    Presentations() {
        return this.getRelated("presentations");
    }

    /**
     *
     * @returns {*}
     */
    Cancellations() {
        return this.getRelated("cancellations");
    }

    /**
     *
     * @returns {*}
     */
    ProposalDownloads() {
        return orderBy(this.getRelated("proposalDownloads"), "id", "desc");
    }

    /**
     *
     * @returns {*}
     */
    PaymentTerms() {
        return orderBy(this.getRelated("paymentTerms"), "due_date", {});
    }

    /**
     *
     * @returns {boolean|*}
     */
    Parent() {
        return this.getRelated("parent");
    }

    /**
     *
     * @returns {number}
     */
    PaymentTermsTotal() {
        let terms = this.constructor
            .query()
            .with("paymentTerms")
            .find(this.id).paymentTerms;
        return Object.keys(terms).reduce((sum, key) => {
            return sum + parseFloat(terms[key].payment_amount);
        }, 0);
    }

    /**
     *
     * @returns {number}
     */
    MarginPrice() {
        let sale = this.sale_price;
        let purchase = this.purchase_price;

        if (sale > 0 && purchase > 0) {
            return sale - purchase;
        }

        return 0;
    }

    /**
     *
     * @returns {string|number}
     */
    MarginPercentage() {
        let sale = this.sale_price;
        let purchase = this.purchase_price;

        if (sale > 0 && purchase > 0) {
            return (((sale - purchase) / sale) * 100).toFixed(2);
        }

        return 0;
    }

    /**
     *
     * @returns {null|methods.printDate}
     */
    FirstTicketPrintDate() {
        const firstTicket = DossierTicket.query()
            .whereHas("proposalTickets", (query) => {
                query.whereHas("group", (query) => {
                    query.where("proposal_id", this.id);
                });
            })
            .orderBy("print_at", "asc")
            .first();

        if (firstTicket) {
            return firstTicket.print_at;
        }

        return null;
    }

    /**
     *
     * @param oldTerms
     * @param newTerms
     */
    static async SynchPaymentTerms(oldTerms, newTerms) {
        oldTerms
            .filter((oldTerm) => {
                return (
                    newTerms.find((newTerm) => oldTerm.id == newTerm.id) ==
                    undefined
                );
            })
            .forEach((deletedTerm) => {
                deletedTerm.api().delete();
            });

        newTerms.forEach((term) => {
            if (term.id) {
                PaymentTerm.api().updateById(term.id, term);
            } else {
                PaymentTerm.api().create(term);
            }
        });
    }

    /**
     *
     * @param leadOpportunityCid
     * @returns {{name: string, id: number} | {name: string, id: number} | {name: string, id: number} | {name: string, id: number}}
     */
    LeadOpportunityById(leadOpportunityCid) {
        return constants.leadOpportunities.find(
            (opportunity) => opportunity.id == leadOpportunityCid,
        );
    }

    createUpsell() {
        return Upsell.api().create({
            proposal_id: this.id,
            proposal_product_id: this.product.id,
            is_upsellable: false,
        });
    }

    sendAgents(sendToAgentIds = [], statuses = {}) {
        return this.constructor
            .api()
            .action(this.id + "/send-agents")
            .update({
                mail_agent_ids: sendToAgentIds,
                statuses: statuses,
            });
    }

    static formatExport(requests = []) {
        return map(requests, (request) => {
            return {
                "Voorstel ID": request.id,
                "Dossier ID": request.dossier.id,
                klant:
                    request.dossier.customer.firstname +
                    " " +
                    request.dossier.customer.lastname,
                Bestemming: request.country
                    ? request.country.label
                    : "onbekend",
                Medewerker: request.dossier.user
                    ? request.dossier.user.name
                    : "geen",
                Reisbureau:
                    request.dossier.customer.company &&
                    request.dossier.customer.company.is_travel_agency
                        ? "Ja"
                        : "Nee",
                "Reis start": request.start_date,
                "Reis eindigt": request.end_date,
                "Email klant": request.dossier.customer.email,
            };
        });
    }

    static formatOpenPaymentExport(requests = []) {
        return map(requests, (request) => {
            let open = request.sale_price;
            if (request.dossier.total_payed !== null) {
                open = (
                    parseFloat(request.sale_price) -
                    parseFloat(request.dossier.total_payed)
                ).toFixed(2);
            }

            return {
                Voorstel: request.id,
                Dossier: request.dossier_id,
                Totaalbedrag: request.sale_price,
                "Betaald bedrag": request.dossier.total_payed,
                "Openstaand bedrag": open,
                "Voorstel aangemaakt": request.created_at,
                "Begin reis": request.dossier.trip_starts_at,
                Medewerker: request.dossier.user
                    ? request.dossier.user.name
                    : "",
            };
        });
    }

    static getReadyForTravelDocumentsCount() {
        return this.api().action("travel-documents").getValue();
    }
}
